import dayjs from "dayjs"
import { smartTextBaseUrl } from "utils/constants"

/**
 * The preview modal promises the reader that Smart Text fields are filled in
 * with example info, so the placeholder chips have to be swapped for sample
 * values before the template is rendered.
 *
 * Template content stores each Smart Text field as an image named after the
 * field's alias - `{url}/employee_first_name.png` as it comes off the server,
 * or the expanded `smartTextBaseUrl` once the editor has seeded the form - so
 * both forms are matched here.
 */

const chip = (value: string) =>
  `<span class="bg-neutral40 px-2 py-1 text-sm border border-neutral500 rounded-md" contenteditable="false">${value}</span>`

const placeholderChip = (label: string) => chip(`&lt;&lt;${label}&gt;&gt;`)

const SAMPLE_EMPLOYER_SIGNATORY = "Ngozi Adeyemi"

/**
 * Dates are relative to today so the example letter always reads as a live
 * offer rather than one that expired months ago.
 */
const sampleDate = (daysFromToday: number) =>
  dayjs().add(daysFromToday, "day").format("D MMM, YYYY")

const getExampleValues = (): Record<string, string> => ({
  company_name: chip("Acme Limited"),
  current_date: chip(sampleDate(0)),
  employee_account_number: chip("0123456789"),
  employee_bank: chip("Providence Bank"),
  employee_birth_date: chip("12 Apr, 1994"),
  employee_compensation: chip("₦12,000,000.00"),
  employee_email: chip("ada.obi@example.com"),
  employee_first_name: chip("Ada"),
  employee_full_name: chip("Ada Obi"),
  employee_gender: chip("Female"),
  employee_last_name: chip("Obi"),
  employee_number: chip("EMP-0142"),
  employee_paygrade: chip("Level 4"),
  employee_payment_method: chip("Bank Transfer"),
  employee_phone: chip("+234 801 234 5678"),
  employee_signature: placeholderChip("Employee Signature"),
  employee_start_date: chip(sampleDate(30)),
  employee_title: chip("Product Designer"),
  employees_manager: chip("Chidi Okafor"),
  employer_signatory_name: chip(SAMPLE_EMPLOYER_SIGNATORY),
  employer_signatory_title: chip("Head of People"),
  employer_signature: `<span style="padding: 3px 6px; font-size: 32px; font-weight: 400;  color: rgb(73, 80, 87);" contenteditable="false" class="!font-autograph">${SAMPLE_EMPLOYER_SIGNATORY}</span>`,
  "full-time_part-time": chip("Full-Time"),
  full_home_address: chip("24 Bourdillon Road, Ikoyi, Lagos, 101233, Nigeria"),
  full_work_address: chip(
    "12 Adeola Odeku Street, Victoria Island, Lagos, 101241, Nigeria"
  ),
  home_address_city: chip("Lagos"),
  home_address_line_1: chip("24 Bourdillon Road"),
  home_address_line_2: chip("Ikoyi"),
  home_address_state: chip("Lagos"),
  home_address_zip: chip("101233"),
  job_title: chip("Product Designer"),
  offer_accepted_date: chip(sampleDate(7)),
  offer_expiration_date: chip(sampleDate(14)),
  offer_sent_date: chip(sampleDate(0)),
  stock_equity: chip("100 shares of common stock vesting over 4 years"),
  work_address_city: chip("Lagos"),
  work_address_line_1: chip("12 Adeola Odeku Street"),
  work_address_line_2: chip("Victoria Island"),
  work_address_state: chip("Lagos"),
  work_address_zip: chip("101241"),
})

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * Matches a Smart Text image however the editor happens to have serialised it -
 * either quote style, self-closing or not, with or without extra attributes -
 * and captures the alias.
 */
const smartTextImage = () =>
  new RegExp(
    `<img[^>]*src=["']?(?:\\{url\\}|${escapeRegExp(
      smartTextBaseUrl
    )})/([\\w-]+)\\.png["']?[^>]*>`,
    "g"
  )

export default function getTemplatePreviewText(content?: string) {
  if (!content) return "<p>No content to preview.</p>"

  const exampleValues = getExampleValues()

  return (
    content
      .replace(
        smartTextImage(),
        (match, alias: string) =>
          // An alias we have no example for keeps its placeholder chip rather
          // than vanishing from the preview.
          exampleValues[alias] ?? match
      )
      // Any Smart Text left standing still needs a resolvable image url.
      .replace(/{url}/g, smartTextBaseUrl)
  )
}
